import { useGetIdentity, useRecordContext, useTranslate } from "ra-core";

import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { useIsMobile } from "@/hooks/use-mobile";
import { CompanyAvatar } from "../companies/CompanyAvatar";
import { RelativeDate } from "../misc/RelativeDate";
import { useGetSalesName } from "../sales/useGetSalesName";
import type { ActivityCompanyNoteCreated, Company } from "../types";
import { useActivityLogContext } from "./ActivityLogContext";
import { ActivityLogNote } from "./ActivityLogNote";

type ActivityLogCompanyNoteCreatedProps = {
  activity: ActivityCompanyNoteCreated;
};

function CompanyLogo() {
  const record = useRecordContext<Company>();
  return <CompanyAvatar width={20} height={20} record={record} />;
}

export function ActivityLogCompanyNoteCreated({
  activity,
}: ActivityLogCompanyNoteCreatedProps) {
  const context = useActivityLogContext();
  const isMobile = useIsMobile();
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const { companyNote } = activity;
  const isCurrentUser = activity.sales_id === identity?.id;
  const salesName = useGetSalesName(activity.sales_id, {
    enabled: !isCurrentUser,
  });
  
  // Use activity.company_id as fallback if companyNote.company_id is undefined
  const companyId = companyNote?.company_id ?? activity.company_id;
  
  if (!companyId) return null;
  
  const link = isMobile
    ? `/companies/${companyId}/notes/${companyNote.id}`
    : `/companies/${companyId}/show`;
  return (
    <ActivityLogNote
      header={
        <div className="flex items-start gap-2 w-full">
          <ReferenceField
            source="company_id"
            reference="companies"
            record={activity.companyNote || { company_id: companyId }}
          >
            <CompanyLogo />
          </ReferenceField>

          <span className="text-muted-foreground text-sm flex-grow">
            {translate(
              isCurrentUser
                ? "crm.activity.you_added_note"
                : "crm.activity.added_note",
              { name: salesName },
            )}{" "}
            <ReferenceField
              source="company_id"
              reference="companies"
              record={activity.companyNote || { company_id: companyId }}
            >
              <TextField source="name" />
            </ReferenceField>
            {context !== "company" && (
              <>
                {" "}
                <RelativeDate date={activity.date} />
              </>
            )}
          </span>

          {context === "company" && (
            <span className="text-muted-foreground text-sm">
              <RelativeDate date={activity.date} />
            </span>
          )}
        </div>
      }
      text={companyNote?.text}
      link={link}
    />
  );
}
